import { useCallback, useEffect, useRef, useState } from "react";
import type { CarouselApi } from "@/components/carousel/carousel";

export interface CarouselAutoplayControls {
  /** User intent: true = autoplay running, false = manually paused. Drives the play/pause button. */
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Trigger a transient pause (e.g. on an arrow click) without changing `isPlaying`. */
  interact: () => void;
}

/**
 * Auto-advances an embla carousel and exposes play/pause controls.
 *
 * Two independent pause concepts, kept separate on purpose:
 * - `isPlaying` — the user's intent (the play/pause button). Stays as-is across
 *   transient interactions, so a drag doesn't flip the button to "play".
 * - transient interaction pause — a temporary stop after a drag / arrow click
 *   that auto-resumes after `pauseOnInteraction` ms.
 *
 * The interval runs only when `api && isPlaying && !interactionPaused`. We do NOT
 * listen to embla's "select" event: autoplay's own scrollNext() fires it, which
 * would make every auto-advance pause itself.
 */
export function useCarouselAutoplay(
  api: CarouselApi | undefined,
  delay: number = 3000,
  pauseOnInteraction: number = 10000,
): CarouselAutoplayControls {
  // Respect reduced-motion: load paused (the user can still press play). Read
  // once via a lazy initializer rather than a setState-in-effect. SSR returns
  // `true`, but nothing rendered depends on this until `api` exists post-mount,
  // so there's no hydration mismatch.
  const [isPlaying, setIsPlaying] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  });
  const [interactionPaused, setInteractionPaused] = useState(false);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Transient pause shared by drag (embla "pointerDown") and `interact()`.
  const beginTransientPause = useCallback(() => {
    setInteractionPaused(true);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      setInteractionPaused(false);
    }, pauseOnInteraction);
  }, [pauseOnInteraction]);

  // Pause autoplay while the user is dragging the carousel.
  useEffect(() => {
    if (!api) return;
    const handlePointerDown = () => beginTransientPause();
    api.on("pointerDown", handlePointerDown);
    return () => {
      api.off("pointerDown", handlePointerDown);
    };
  }, [api, beginTransientPause]);

  // The auto-advance interval.
  useEffect(() => {
    if (!api || !isPlaying || interactionPaused) return;
    const id = setInterval(() => {
      api.scrollNext();
    }, delay);
    return () => clearInterval(id);
  }, [api, isPlaying, interactionPaused, delay]);

  // Clear the resume timeout on unmount.
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);
  const toggle = useCallback(() => setIsPlaying((p) => !p), []);

  return { isPlaying, play, pause, toggle, interact: beginTransientPause };
}
